import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Menu,
  Dropdown,
  Popover,
  Spin,
  Button,
  AutoComplete,
  Drawer, // Import Drawer
  Layout,
  Tooltip,
  Grid,
  message
} from 'antd';
import {
  SettingOutlined,
  LogoutOutlined,
  CloseOutlined,
  UserOutlined,
  SearchOutlined,
  DownloadOutlined,
  SendOutlined,
  PlusOutlined,
  MenuOutlined,
  UnorderedListOutlined,
  TableOutlined,
  AppstoreAddOutlined,
  BarChartOutlined,
  LineChartOutlined,
  CalendarOutlined,
  FileTextOutlined, // Import MenuOutlined for the hamburger icon
  HistoryOutlined
} from '@ant-design/icons';
import logoIcon from '@/style/images/logo.png';
import logoText from '@/style/images/logo-text.png';
import { selectAuth } from '@/redux/auth/selectors';
import history from '@/utils/history';
import uniqueId from '@/utils/uinqueId';
import capitalizeFirstLetter from '@/utils/stringHelpers';
import Workbook from '@/components/Kanban/Workbook/Workbook';
import { useLocation } from 'react-router-dom';
import Dashboard from '@/pages/Dashboard/Dashboard';
import {
  useUserData,
  useHistoryData,
  useWorkstreamData,
  useWorkstreamsData,
  useMilestoneData,
  useAuditHistoryData,
  useSubsectorData,
} from '@/components/Kanban/Workbook/DataHooks';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import checkToFinanceApprovalStatus from '@/utils/Sort/checkTOFinanceApprovalStatus';
import getNetRecurringBenefitCategory from '@/utils/Sort/getNetRecurringBenefitCatagory';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import determineStage from '@/utils/DetermineStage';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import LLatestActualDate from '@/utils/Sort/checkDate/LLatestActualDate';
import {
  L0ActualDate,
  L1ActualDate,
  L2ActualDate,
  L3ActualDate,
  L4ActualDate,
  L5ActualDate,
  SubmittedForL1PlanDate,
  SubmittedForL2PlanDate,
  SubmittedForL3PlanDate,
  SubmittedForL4PlanDate,
  SubmittedForL5PlanDate,
  L2PlanDate,
  L3PlanDate,
  L4PlanDate,
  L5PlanDate,
  L1OriginalPlanDate,
  L2OriginalPlanDate,
  L3OriginalPlanDate,
  L4OriginalPlanDate,
  L5OriginalPlanDate
} from '@/utils/Sort/checkDate/getDate';
import getNetRecurringBenefitChangeCategoryL0Plus from '@/utils/color/getNetRecurringBenefitCatagoryL0Plus';
import getNetRecurringBenefitChangeCategoryL1Plus from '@/utils/color/getNetRecurringBenefitCatagoryL1Plus';
import getNetRecurringBenefitChangeCategoryL2Plus from '@/utils/color/getNetRecurringBenefitCatagoryL2Plus';
import getNetRecurringBenefitChangeCategoryL3Plus from '@/utils/color/getNetRecurringBenefitCatagoryL3Plus';
import getNetRecurringBenefitChangeCategoryL4Plus from '@/utils/color/getNetRecurringBenefitCatagoryL4Plus';
import getComplianceStatus from '@/utils/Sort/complianceCheck';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';
import sortInitiativesByActualImpact from '@/utils/Sort/sortByActualImpact';
import { calculateTotalNetRecurringBenefit } from '@/utils/calculations';
import { determinePlanningSummary } from '@/utils/planningSummary';
import groupInitiativesBy from '@/utils/group/groupInitiatives';
import groupImpactsByPeriod from '@/utils/group/groupImpact';
import groupWorkstreamActionsBy from '@/utils/group/groupActionItems';
import groupMilestonesBy from '@/utils/group/groupMilestones';
import handleColoring from '@/utils/color/handleColoring';
import pendingApprovalForWorkstreamLead from '@/utils/pendingApproval/pendingApprovalForWorkstreamLead';
import pendingApprovalForToFinance from '@/utils/pendingApproval/pendingApprovalForToFinance';
import pendingApprovalForStrategyAnalyst from '@/utils/pendingApproval/pendingApprovalForStrategyAnalyst';
import determineInitiativesPlanningSummary from '@/utils/filters/planningSummaryForInitiatives';
import SubmittedForLApproval from '@/utils/filters/SubmittedForLApproval';
import filterInitiativesByImpactUpdate2025 from '@/utils/filters/updateCheckFor2025';
import calculateNetRecurringBenefits from '@/utils/pendingApproval/netRecurringBenefitRange';
import getImplementationCostRange from '@/utils/pendingApproval/getImplementationCost';
import calculatePlanningSummary from '@/utils/filters/planningSummarySimplified';
import calculateNetRecurringBenefitsWithInitiativesByRange from '@/utils/filters/calculateNetRecurringBenfitCurrentVsLastWeek';
import pendingApprovalForWorkstreamSponsor from '@/utils/pendingApproval/pendingApprovalForWorkstreamSponsor';
import groupInitiativesByLastWeekWeeklyStatus from '@/utils/filters/lastWeekWeeklyStatus';
import groupInitiativesByNetRecurringBenefitRangeL4Plus from '@/utils/filters/L4PlusVariance';
import groupInitiativesByNetRecurringBenefitRangeL3Plus from '@/utils/filters/L3PlusVariance';
import groupInitiativesByNetRecurringBenefitRangeL2Plus from '@/utils/filters/L2PlusVariance';
import groupInitiativesByNetRecurringBenefitRangeL1Plus from '@/utils/filters/L1PlusVariance';
import groupInitiativesByNetRecurringBenefitRangeL0Plus from '@/utils/filters/L0PlusVariance';
import ImplementationCostCurrentVsLastweek from '@/utils/filters/ImplementationCostCurrentVsLastweek';
import calculateOneTimeBenefitCurrentVsLastweek from '@/utils/filters/calculateOneTimeBenefitCurrentVsLastweek';
import LWStage from '@/utils/filters/LWStage';
import determineLWPlanningSummary from '@/utils/filters/LWPlanningSummary';
import l3AndL4DateComplianceCheck from '@/utils/filters/complianceDateFilter';
import filterInitiativesByActuals from '@/utils/filters/filterInitiativesByImpactActual';
import filterInitiativesByImpactActuals from '@/utils/filters/filterInitiativesByActualImpact';
import { filterInitiativeByCancellationDate } from '@/utils/filters/filterByCancellationDate';
import { filterApprovalDates, filterImpactDate, filterTimelineDates } from '@/utils/filters/dataFilters';
import filterMilestoneByPlanningSummary from '@/utils/filters/filterMilestonesByPlanningSummary';
import filterMilestoneByPlanningSummarySimpified from '@/utils/filters/filterMilestonePlanningSummarySimplified';
import filterMilestonesByLWPlanningSummary from '@/utils/filters/filterMilestonesByLWPlanningSummary';
import { filterImpactAttributes, filterKPIAttributes } from '@/utils/filters/impactMilestoneKpiFilter';
import filterInitiativesByImpactUpdate from '@/utils/filters/actualUpdateCheck';
import {
  applyInitiativeSort,
  applyMilestoneSort,
  applyWorkstreamActionSort,
} from '@/utils/Sort/sortingFunction';
import { useSearch } from '@/components/Kanban/Workbook/useSearch';
import { useDownloadData } from './handleDownload';
import planningSummarySimplified from '@/utils/planningSummarySimplified';
import { processedKPIData, processImpactData } from '@/utils/filters/processImpactData';
import groupInitiativesByYearActual from '@/utils/Sort/groupInitiativesByYearActual';
import AddMilestoneModal from '@/components/Kanban/MilestoneModal';
import { createPlanningSummaryLookup, createPlanningSummarySimplifiedLookup } from '@/utils/filters/planningSummaryGroup';
import WorkstreamActionModal from '@/components/Kanban/Workstreamaction/WorkstreamactionModal';
import moment from 'moment';
const { Header } = Layout;
const { useBreakpoint } = Grid;
export default function HeaderContent({
  initiativesData,
  workstreamActionsData,
}) {
  const currentUser = useSelector(selectAuth);
  const location = useLocation();
  const usersData = useUserData();
  const screens = useBreakpoint();
  const workstreamsData = useWorkstreamData();
  const workstreamData = useWorkstreamsData();
  const historyData = useHistoryData();
  const { auditHistory, loadingHistory, hasMore, fetchHistory } = useAuditHistoryData();
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isWorkbookVisible, setIsWorkbookVisible] = useState(false);
  const [isDashboardVisible, setIsDashboardVisible] = useState(false);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [sortField, setSortField] = useState('initiativeNumber'); // Default to initiativeNumber
  const [sortOrder, setSortOrder] = useState('desc'); // Default to descending
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilter, setCurrentFilter] = useState(null);
  const [impactCurrentFilter, setImpactCurrentFilter] = useState(null);
  const [kpiCurrentFilter, setKpiCurrentFilter] = useState(null);
  const [milestoneCurrentFilter, setMilestoneCurrentFilter] = useState(null);
  const [periodCurrentFilter, setPeriodCurrentFilter] = useState(null);
  const [actionCurrentFilter, setActionCurrentFilter] = useState(null);
  const [attributeFilters, setAttributeFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedImpactGroup, setSelectedImpactGroup] = useState(null);
  const [selectedHistoryGroup, setSelectedHistoryGroup] = useState([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('Manual selection');
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [selectedRange, setSelectedRange] = useState([]);
  const [filteredMilestones, setFilteredMilestones] = useState([]);
  const historyPopoverContentRef = useRef(null);
  const [filteredActionItems, setFilteredActionItems] = useState([]);
  const [groupedInitiativesByHistory, setGroupedInitiativesByHistory] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('None');
  const [colorByAttribute, setColorByAttribute] = useState(null);
  const [initiativeColors, setInitiativeColors] = useState({});
  const [milestoneColors, setMilestoneColors] = useState({});
  const [actionItemColors, setActionItemColors] = useState({});
  const [groupedInitiatives, setGroupedInitiatives] = useState({});
  const [groupedMilestones, setGroupedMilestones] = useState({});
  const [groupedActionItems, setGroupedActionItems] = useState({});
  const [groupedImpact, setGroupedImpact] = useState({});
  const [workbookData, setWorkbookData] = useState(null);
  const [loadingWorkbook, setLoadingWorkbook] = useState(false);
  const [visible, setVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [finalTotals, setFinalTotals] = useState({});
  const [sumVisible, setSumVisible] = useState(true);
  const [filterHistory, setFilterHistory] = useState([]);
  const executiveAssistant = currentUser?.role?.name === 'Executive Assistant';

  const [selectedInfoMenu, setSelectedInfoMenu] = useState(
    executiveAssistant ? 'workstreamactions' : 'initiatives'
  );
  const [xAxis, setXAxis] = useState('stage');
  const [yAxis, setYAxis] = useState('Number of Initiatives');
  const [filteredImpact, setFilteredImpact] = useState([]);
  const [sumResults, setSumResults] = useState({}); // Move sumResults to state
  const [selectedMenu, setSelectedMenu] = useState({
    key: 'List',
    icon: <UnorderedListOutlined />,
    label: 'List View',
  });
  const [aggregatedAttributes, setAggregatedAttributes] = useState(
    selectedMenu.key === 'Table' ? ['initiativeCount'] : []
  );
  useEffect(() => {
    if (selectedMenu.key === 'Table') {
      // ONLY default to 'initiativeCount' if aggregatedAttributes is currently empty
      if (aggregatedAttributes.length === 0) {
        setAggregatedAttributes(['initiativeCount']);
      }
    }
  }, [selectedMenu.key, aggregatedAttributes.length, setAggregatedAttributes]);
  const subsectorData = useSubsectorData();
  const KEY_MAP = {
    selectedMenu: 'm',
    selectedInfoMenu: 'i',
    sumVisible: 'v',
    aggregatedAttributes: 'a',
    attributeFilters: 'f',
    colorByAttribute: 'c',
    selectedGroup: 'g',
    currentFilter: 't',
    sortField: 's',
    sortOrder: 'o'
  };

  const REVERSE_MAP = Object.fromEntries(Object.entries(KEY_MAP).map(([k, v]) => [v, k]));

  const menuMap = {
    'List': { key: 'List', icon: <UnorderedListOutlined />, label: 'List View' },
    'Table': { key: 'Table', icon: <TableOutlined />, label: 'Table View' },
    'Pivot': { key: 'Pivot', icon: <AppstoreAddOutlined />, label: 'Pivot View' },
    'Gantt': { key: 'Gantt', icon: <CalendarOutlined />, label: 'Gantt Chart' },
    'Stacked': { key: 'Stacked', icon: <BarChartOutlined />, label: 'Stacked Chart' },
    'Waterfall': { key: 'Waterfall', icon: <FileTextOutlined />, label: 'Waterfall Chart' },
    'Multi-Measure': { key: 'Multi-Measure', icon: <LineChartOutlined />, label: 'Multi-Measure Chart' },
  };

  /**
   * ULTRA COMPRESSION LOGIC
   */
  const handleCopyLinkClick = () => {
    try {
      // Start with a clean URL (No long strings)
      const url = new URL(window.location.origin + window.location.pathname);

      // Build a tiny object using the KEY_MAP
      const tinyState = {};
      if (selectedMenu?.key) tinyState[KEY_MAP.selectedMenu] = selectedMenu.key;
      if (selectedInfoMenu) tinyState[KEY_MAP.selectedInfoMenu] = selectedInfoMenu;
      if (sumVisible !== undefined) tinyState[KEY_MAP.sumVisible] = sumVisible ? 1 : 0;
      if (aggregatedAttributes?.length) tinyState[KEY_MAP.aggregatedAttributes] = aggregatedAttributes;
      if (Object.keys(attributeFilters || {}).length) tinyState[KEY_MAP.attributeFilters] = attributeFilters;
      if (colorByAttribute) tinyState[KEY_MAP.colorByAttribute] = colorByAttribute;
      if (selectedGroup && selectedGroup !== 'None') tinyState[KEY_MAP.selectedGroup] = selectedGroup;
      if (currentFilter) tinyState[KEY_MAP.currentFilter] = currentFilter;
      if (sortField) tinyState[KEY_MAP.sortField] = sortField;
      if (sortOrder) tinyState[KEY_MAP.sortOrder] = sortOrder === 'desc' ? 1 : 0;

      // Convert to a mini-string and Base64 encode
      const compressed = btoa(unescape(encodeURIComponent(JSON.stringify(tinyState))));

      // Set only ONE parameter: "q" (for query)
      url.searchParams.set('q', compressed);
      url.searchParams.set('section', 'workbook'); // Keep route requirement

      const shortUrl = url.toString();

      // Robust Copy Method (Handles the "writeText" undefined error)
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(shortUrl).then(() => {
          message.success('Ultra-short link copied!');
        });
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = shortUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success('Short link copied (fallback)!');
      }
    } catch (err) {
      console.error('Shortening failed:', err);
    }
  };

  /**
   * DECOMPRESSION LOGIC
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');

    if (q) {
      try {
        const decoded = JSON.parse(decodeURIComponent(escape(atob(q))));

        // Map keys back to states
        if (decoded[KEY_MAP.selectedMenu] && menuMap[decoded[KEY_MAP.selectedMenu]]) {
          setSelectedMenu(menuMap[decoded[KEY_MAP.selectedMenu]]);
        }
        if (decoded[KEY_MAP.selectedInfoMenu]) setSelectedInfoMenu(decoded[KEY_MAP.selectedInfoMenu]);
        if (decoded[KEY_MAP.sumVisible] !== undefined) setSumVisible(decoded[KEY_MAP.sumVisible] === 1);
        if (decoded[KEY_MAP.aggregatedAttributes]) setAggregatedAttributes(decoded[KEY_MAP.aggregatedAttributes]);
        if (decoded[KEY_MAP.attributeFilters]) setAttributeFilters(decoded[KEY_MAP.attributeFilters]);
        if (decoded[KEY_MAP.colorByAttribute]) setColorByAttribute(decoded[KEY_MAP.colorByAttribute]);
        if (decoded[KEY_MAP.selectedGroup]) setSelectedGroup(decoded[KEY_MAP.selectedGroup]);
        if (decoded[KEY_MAP.currentFilter]) setCurrentFilter(decoded[KEY_MAP.currentFilter]);
        if (decoded[KEY_MAP.sortField]) setSortField(decoded[KEY_MAP.sortField]);
        if (decoded[KEY_MAP.sortOrder] !== undefined) {
          setSortOrder(decoded[KEY_MAP.sortOrder] === 1 ? 'desc' : 'asc');
        }
      } catch (e) {
        console.error("Link decode error:", e);
      }
    }
  }, []);

  const { milestones: allMilestones } = useMilestoneData(initiativesData, currentUser);
  const getNameToSort = (item, field) => {
    return {
      firstName: item?.[field]?.[0]?.firstName || '',
      lastName: item?.[field]?.[0]?.lastName || '',
    };
  };
  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    const array = new Uint8Array(6);
    crypto.getRandomValues(array);
    return '#' + Array.from(array, n => letters[n % 16]).join('');
  };
  useEffect(() => {
    if (Object.entries(attributeFilters).length > 0) {
      if (attributeFilters.hasOwnProperty('period')) {
        const periodFilter = attributeFilters.period;
        const fromDate = periodFilter.fromDate ? periodFilter.fromDate._d : null;
        const toDate = periodFilter.toDate ? periodFilter.toDate._d : null;
        setSelectedDateRange([fromDate, toDate]);
      }
    }
  }, [attributeFilters, setSelectedDateRange]);
  const hasInitiativeAttributeFilters = Object.entries(attributeFilters).some(([key, value]) =>
    [
      'initiativeName', 'initiativeNumber', 'stage', 'weeklyStatus', 'hasFinancialImpact', 'forSpecificCustomer', 'segmentalPBT', 'selectedForToMeetingDescusion', 'Planning summary', 'Planning summary simplified', 'initiativeNumber - initiativeName',
      'Has qualitative KPI?', 'L4 date compliance check (forecast vs planned date)', 'L3 date compliance check (forecast vs planned date)',
      '(FY24-25) Actual impact update check', '(FY23-24) Actual impact update check',
      '(FY25-26) Actual impact update check', 'Actual impact update check (for last completed and previous period)',
      'Actual KPI update check (for last completed and previous period)', 'Cancellation date',
      'Latest actual date (All L gates)', 'L1 planned date', 'L2 planned date', 'L3 planned date',
      'L4 planned date', 'L5 planned date', 'L0 actual date', 'L1 actual date', 'L2 actual date',
      'L3 actual date', 'L4 actual date', 'L5 actual date', 'Sub . for L1 approv . date',
      'Sub . for L2 approv . date', 'Sub . for L3 approv . date', 'Sub . for L4 approv . date',
      'Sub . for L5 approv . date', 'Sub . back to L3 approv . date', 'L1 Original Planned Date',
      'L2 Original Planned Date', 'L3 Original Planned Date', 'L4 Original Planned Date', 'L5 Original Planned Date',
      'Planned value last update date (calendarized impact)', 'Actual value last update date (calendarized impact)',
      'initiativeOwnerInfo', 'initiativeOwnerBackupInfo', 'workstreamSponsorInfo', 'workstreamLeadInfo',
      'AllowOnlyTo', 'workstreamSponsorWorkstream', 'workstreamLeadSubworkstream',
      'crossFunctionalSupportWorkstreamInfo', 'stageSimplified', 'stage', 'strategyInfo',
      'Workstream lead pending approval', 'Workstream sponsor pending approval', 'TO finance pending approval',
      'Strategy analyst pending approval', 'Workstream lead pending approvers',
      'Workstream sponsor pending approvers', 'TO finance pending approvers',
      'Strategy analyst pending approvers', 'Net recurring benefit range', 'Future implementation costs range',
      'Pipeline weekly variance (L0+)', 'Pipeline weekly variance (L1+)', 'Pipeline weekly variance (L2+)',
      'Pipeline weekly variance (L3+)', 'Pipeline weekly variance (L4+)', '(LW) Weekly status',
      '(LW) Stage', '(LW) Planning summary',
      'Change in net recurring benefits (binned, current vs last week, latest estimate, annualized)',
      'Change in One Time Benefit (binned, current vs last week, latest estimate, annualized)',
      'Change in implementation costs (binned, latest estimate, annualized)',
      'FY 2023/24 actual impact update check', 'FY 2024/25 actual impact update check',
      'Number of attachments', 'frequencyAndDirection', 'impactNumber', 'typeOfImpact', 'sector', 'subsector',
      'Whose P&L does this impact?', 'TypeOfKPI', 'UniversalKPI', 'unitOfMeasurement', 'aggregationMode', 'Description', 'kpiSubsector', 'kpiSector',
      'Type', 'size', 'initiativeCategory', 'sectorInfo', 'subsectorInfo', 'segment'
    ].includes(key) && value !== undefined
  );
  const hasMilestoneAttributeFilters = Object.entries(attributeFilters).some(([key, value]) =>
    [
      'Owner', 'milestoneNumber', 'type', 'name', 'Milestone Status',
      'Milestone Planning summary', 'Planning summary (simplified)', '(LW) Planning summary',
      'planningCheck', 'Work starting date', 'Milestone completion date',
      'Created by', 'Updated by', 'Change in Milestone completion date (binned, current vs last week)',
      'milestoneNumber - name', 'Updated On', 'Created On'
    ].includes(key) && value !== undefined
  );

  const hasActionItemAttributeFilters = Object.entries(attributeFilters).some(([key, value]) =>
    [
      'workstreamActionName', 'actionItemNumber', 'status', 'source', 'planningSummary', 'owner', 'collaborator',
      'Accountable Workstream', 'Accountable subworkstream', 'Created on', 'Updated on', 'createdBy', 'Updated By'
    ].includes(key) && value !== undefined
  );
  const INITIATIVE_FILTER_KEYS = [
    'initiativeName', 'initiativeNumber', 'stage', 'weeklyStatus', 'hasFinancialImpact', 'forSpecificCustomer', 'segmentalPBT', 'selectedForToMeetingDescusion', 'Planning summary', 'Planning summary simplified', 'initiativeNumber - initiativeName',
    'Has qualitative KPI?', 'L4 date compliance check (forecast vs planned date)', 'L3 date compliance check (forecast vs planned date)',
    '(FY24-25) Actual impact update check', '(FY23-24) Actual impact update check',
    '(FY25-26) Actual impact update check', 'Actual impact update check (for last completed and previous period)',
    'Actual KPI update check (for last completed and previous period)', 'Cancellation date',
    'Latest actual date (All L gates)', 'L1 planned date', 'L2 planned date', 'L3 planned date',
    'L4 planned date', 'L5 planned date', 'L0 actual date', 'L1 actual date', 'L2 actual date',
    'L3 actual date', 'L4 actual date', 'L5 actual date', 'Sub . for L1 approv . date',
    'Sub . for L2 approv . date', 'Sub . for L3 approv . date', 'Sub . for L4 approv . date',
    'Sub . for L5 approv . date', 'Sub . back to L3 approv . date', 'L1 Original Planned Date',
    'L2 Original Planned Date', 'L3 Original Planned Date', 'L4 Original Planned Date', 'L5 Original Planned Date',
    'Planned value last update date (calendarized impact)', 'Actual value last update date (calendarized impact)',
    'initiativeOwnerInfo', 'initiativeOwnerBackupInfo', 'workstreamSponsorInfo', 'workstreamLeadInfo',
    'AllowOnlyTo', 'workstreamSponsorWorkstream', 'workstreamLeadSubworkstream',
    'crossFunctionalSupportWorkstreamInfo', 'stageSimplified', 'stage', 'strategyInfo',
    'Workstream lead pending approval', 'Workstream sponsor pending approval', 'TO finance pending approval',
    'Strategy analyst pending approval', 'Workstream lead pending approvers',
    'Workstream sponsor pending approvers', 'TO finance pending approvers',
    'Strategy analyst pending approvers', 'Net recurring benefit range', 'Future implementation costs range',
    'Pipeline weekly variance (L0+)', 'Pipeline weekly variance (L1+)', 'Pipeline weekly variance (L2+)',
    'Pipeline weekly variance (L3+)', 'Pipeline weekly variance (L4+)', '(LW) Weekly status',
    '(LW) Stage', '(LW) Planning summary',
    'Change in net recurring benefits (binned, current vs last week, latest estimate, annualized)',
    'Change in One Time Benefit (binned, current vs last week, latest estimate, annualized)',
    'Change in implementation costs (binned, latest estimate, annualized)',
    'FY 2023/24 actual impact update check', 'FY 2024/25 actual impact update check',
    'Number of attachments', 'frequencyAndDirection', 'impactNumber', 'typeOfImpact', 'sector', 'subsector',
    'Whose P&L does this impact?', 'TypeOfKPI', 'UniversalKPI', 'unitOfMeasurement', 'aggregationMode', 'kpiSubsector', 'kpiSector',
    'Planning summary', 'Planning summary simplified', 'Description',
    'Type', 'size', 'initiativeCategory', 'sectorInfo', 'subsectorInfo', 'segment'
  ];
  const MILESTONE_FILTER_KEYS = [
    'Owner', 'milestoneNumber', 'type', 'name', 'Milestone Status',
    'Milestone Planning summary', 'Planning summary (simplified)', '(LW) Planning summary',
    'planningCheck', 'Work starting date', 'Milestone completion date',
    'Created by', 'Updated by', 'Change in Milestone completion date (binned, current vs last week)',
    'milestoneNumber - name', 'Updated On', 'Created On'
  ];
  const ACTION_ITEM_FILTER_KEYS = [
    'workstreamActionName', 'actionItemNumber', 'status', 'source', 'planningSummary', 'owner', 'collaborator',
    'Accountable Workstream', 'Accountable subworkstream', 'Created on', 'Updated on', 'createdBy', 'Updated By'
  ];
  useEffect(() => {
    applyFiltersAndSort(
      initiativesData,
      allMilestones,
      workstreamActionsData,
    );
  }, [
    initiativesData,
    allMilestones,
    searchTerm,
    sortField,
    sortOrder,
    selectedFilters,
    selectedGroup,
    selectedHistoryGroup,
    attributeFilters,
    colorByAttribute,
    milestoneCurrentFilter,
    actionCurrentFilter,
    workstreamActionsData, // Dependency for action items
    selectedImpactGroup,
    selectedTimePeriod,
    selectedDateRange,
  ]);
  const applyFiltersAndSort = (
    currentInitiatives,
    allAvaliableMilestones,
    allAvaliableActionItem
  ) => {
    let filtered = [...currentInitiatives];
    let result = [...allAvaliableMilestones];
    let actionResult = [...allAvaliableActionItem];
    let impactResult = filtered.flatMap((initiative) => initiative.impact || []);
    let kpiResultData = filtered.flatMap((initiative) => initiative.KPI || []);
    const { filteredImpact, sumResults } = processImpactData(impactResult, attributeFilters);
    const { filteredKPI, kpiResult } = processedKPIData(kpiResultData, attributeFilters);
    let determineImpactOrKPI = filteredImpact || filteredKPI;
    let determineResult = sumResults || kpiResult;
    const currentDate = new Date();
    const approvalStatuses = {
      workstreamLead: pendingApprovalForWorkstreamLead({ initiatives: filtered }),
      sponsor: pendingApprovalForWorkstreamSponsor({ initiatives: filtered }),
      toFinance: pendingApprovalForToFinance({ initiatives: filtered }), // Assuming this function exists
      strategyAnalyst: pendingApprovalForStrategyAnalyst({ initiatives: filtered }), // Assuming this function exists
    };

    const initiativesWithAction = SubmittedForLApproval(filtered);
    const initiativesPlanningSummary =
      determineInitiativesPlanningSummary(filtered);
    const planningSummaryLookup = createPlanningSummaryLookup(initiativesPlanningSummary);
    const initiativesNetRecurringBenefits =
      calculateNetRecurringBenefits(filtered);
    const initiativesImplementationCostRange =
      getImplementationCostRange(filtered);
    const initiativesSimplifiedPlanning = calculatePlanningSummary(
      filtered,
      currentDate.toISOString()
    );
    const planningSummarySimplifiedLookup = createPlanningSummaryLookup(initiativesSimplifiedPlanning);
    const updateCheck = filterInitiativesByImpactUpdate(filtered);
    const updateCheck2025 = filterInitiativesByImpactUpdate2025(filtered);
    const initiativesCurrentVsLastWeek =
      calculateNetRecurringBenefitsWithInitiativesByRange(filtered);
    const initiativesOneTimeBenefitCurrentVsLastweek =
      calculateOneTimeBenefitCurrentVsLastweek(filtered);
    const initiativesImplementation =
      ImplementationCostCurrentVsLastweek(filtered);
    const initiativesL0Group =
      groupInitiativesByNetRecurringBenefitRangeL0Plus(filtered);
    const initiativesL1Group =
      groupInitiativesByNetRecurringBenefitRangeL1Plus(filtered);
    const initiativesLastWeeklyStatus =
      groupInitiativesByLastWeekWeeklyStatus(filtered);
    const initiativesLWPlanningSummary =
      determineLWPlanningSummary(filtered);
    const lastWeekStage = LWStage(filtered);
    const initiativesL2Group = groupInitiativesByNetRecurringBenefitRangeL2Plus(
      filtered
    );
    const initiativesL3Group = groupInitiativesByNetRecurringBenefitRangeL3Plus(
      filtered
    );
    const initiativesL4Group = groupInitiativesByNetRecurringBenefitRangeL4Plus(
      filtered
    );

    const workstreamLeadPendingMap = new Map(
      Object.values(approvalStatuses.workstreamLead.pending || {}).flat().map(i => [i._id, i])
    );
    const workstreamLeadApprovedMap = new Map(
      Object.values(approvalStatuses.workstreamLead.approved || {}).flat().map(i => [i._id, i])
    );
    const workstreamLeadNotSetMap = new Map(
      Object.values(approvalStatuses.workstreamLead.notSet || {}).flat().map(i => [i._id, i])
    );

    // For Workstream Sponsor - **CORRECTION HERE**
    const sponsorPendingMap = new Map(
      Object.values(approvalStatuses.sponsor.pendingSponsor || {}).flat().map(i => [i._id, i])
    );
    const sponsorApprovedMap = new Map(
      Object.values(approvalStatuses.sponsor.approvedSponsor || {}).flat().map(i => [i._id, i])
    );
    const sponsorNotSetMap = new Map(
      Object.values(approvalStatuses.sponsor.notSetSponsor || {}).flat().map(i => [i._id, i])
    );

    // For To Finance (assuming similar structure from pendingApprovalForToFinance)
    const toFinancePendingMap = new Map(
      Object.values(approvalStatuses.toFinance.pendingTO || {}).flat().map(i => [i._id, i])
    );
    const toFinanceApprovedMap = new Map(
      Object.values(approvalStatuses.toFinance.approvedTO || {}).flat().map(i => [i._id, i])
    );
    const toFinanceNotSetMap = new Map(
      Object.values(approvalStatuses.toFinance.notSetTO || {}).flat().map(i => [i._id, i])
    );

    // For Strategy Analyst (assuming similar structure from pendingApprovalForStrategyAnalyst)
    const strategyAnalystPendingMap = new Map(
      Object.values(approvalStatuses.strategyAnalyst.pendingAnalyst || {}).flat().map(i => [i._id, i])
    );
    const strategyAnalystApprovedMap = new Map(
      Object.values(approvalStatuses.strategyAnalyst.approvedAnalyst || {}).flat().map(i => [i._id, i])
    );
    const strategyAnalystNotSetMap = new Map(
      Object.values(approvalStatuses.strategyAnalyst.notSetAnalyst || {}).flat().map(i => [i._id, i])
    );
    const pendingInitiativeIds = new Set(Array.from(workstreamLeadPendingMap.keys()));
    const approvedInitiativeIds = new Set(Array.from(workstreamLeadApprovedMap.keys()));
    const notSetInitiativeIds = new Set(Array.from(workstreamLeadNotSetMap.keys()));
    const pendingSponsorIds = new Set(Array.from(sponsorPendingMap.keys()));
    const approvedSponsorIds = new Set(Array.from(sponsorApprovedMap.keys()));
    const notSetSponsorIds = new Set(Array.from(sponsorNotSetMap.keys()));
    const pendingTOIds = new Set(Array.from(toFinancePendingMap.keys()));
    const approvedTOIds = new Set(Array.from(toFinanceApprovedMap.keys()));
    const notSetTOIds = new Set(Array.from(toFinanceNotSetMap.keys()));
    const pendingAnalystIds = new Set(Array.from(strategyAnalystPendingMap.keys()));
    const approvedAnalystIds = new Set(Array.from(strategyAnalystApprovedMap.keys()));
    const notSetAnalystIds = new Set(Array.from(strategyAnalystNotSetMap.keys()));
    if (Object.keys(selectedFilters).length > 0 || hasInitiativeAttributeFilters || Object.keys(attributeFilters).length > 0) {
      filtered = filtered.filter((initiative) => {
        let filterPass = true;
        // Apply selectedFilters (e.g., impact, milestone)
        if (Object.keys(selectedFilters).length > 0) {
          filterPass = Object.entries(selectedFilters).every(([key, value]) => {
            if (Array.isArray(value)) {
              if (Array.isArray(initiative[key])) {
                return initiative[key].some((item) => value.includes(item));
              }
              return value.includes(initiative[key]);
            }

            return true;
          });
          if (!filterPass) return false;
        }
        for (const [key, value] of Object.entries(attributeFilters)) {
          // Skip filters explicitly handled by milestone or action item sections
          if (MILESTONE_FILTER_KEYS.includes(key) || ACTION_ITEM_FILTER_KEYS.includes(key)) {
            continue;
          }
          if (!INITIATIVE_FILTER_KEYS.includes(key)) {
            continue;
          }
          if (value === undefined) {
            continue; // 'Select all' for this filter
          }
          if (Array.isArray(value) && value.length === 0) {
            return false; // Empty array means "show nothing"
          }
          // Centralized helper for handling array of objects (owners, sponsors, etc.)
          const handleArrayOfObjects = (
            arr,
            value, // Pass the filter 'value' array as an argument
            checkSubworkstream = false,
            checkWorkstream = false
          ) => {
            if (!arr || !Array.isArray(arr) || arr.length === 0) {
              return value.includes('<not set>');
            }
            return arr.some((item) => {
              // Check for workstream and subworkstream names
              if (checkSubworkstream && item.subworkstreamName) {
                return value.includes(item.subworkstreamName);
              }
              if (checkWorkstream && item.workstreamName) {
                return value.includes(item.workstreamName);
              }
              const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
              if (fullName) {
                return value.includes(fullName);
              }
              return value.includes('<not set>');
            });
          };
          switch (key) {
            case 'Planning summary':
              const initiativeGroups = planningSummaryLookup.get(initiative._id);
              if (!initiativeGroups || !value.some(group => initiativeGroups.has(group))) {
                filterPass = false;
              }
              break;
            case 'Planning summary simplified':
              const simplifiedGroups = planningSummarySimplifiedLookup.get(initiative._id);
              if (!simplifiedGroups || !value.some(group => simplifiedGroups.has(group))) {
                filterPass = false;
              }
              break;
            case 'initiativeNumber - initiativeName':
              const initiativeName = initiative.initiativeName || '';
              const initiativeNumber = initiative.initiativeNumber || '';
              const combinedValue = `${initiativeNumber} - ${initiativeName}`;
              if (!value.includes(combinedValue)) {
                filterPass = false;
              }
              break;
            case 'L4 date compliance check (forecast vs planned date)':
            case 'L3 date compliance check (forecast vs planned date)':
              if (!l3AndL4DateComplianceCheck(initiative, value, key)) {
                filterPass = false;
              }
              break;
            case 'Has qualitative KPI?':
              if (!initiative.KPI || initiative.KPI.length === 0) {
                filterPass = value.includes('<not set>');
              } else {
                filterPass = value.includes(initiative.KPI.some((kpi) => kpi.isFinancial));
              }
              break;
            case '(FY24-25) Actual impact update check':
            case '(FY23-24) Actual impact update check':
            case '(FY25-26) Actual impact update check':
              if (!filterInitiativesByActuals(initiative, value, key)) {
                filterPass = false;
              }
              break;
            case 'Actual impact update check (for last completed and previous period)':
              if (!filterInitiativesByImpactActuals(initiative, value, 'Actual impact update check (for last completed and previous period)')) {
                filterPass = false;
              }
              break;
            case 'Actual KPI update check (for last completed and previous period)':
              if (!filterInitiativesByImpactActuals(initiative, value, 'Actual KPI update check (for last completed and previous period)')) {
                filterPass = false;
              }
              break;
            case 'Cancellation date':
              if (!filterInitiativeByCancellationDate(initiative, value)) {
                filterPass = false;
              }
              break;
            case 'Latest actual date (All L gates)':
              if (value.fromDate && value.toDate) {
                const fromDate = value.fromDate._d;
                const toDate = value.toDate._d;
                let initiativeDate;
                const stageKey = initiative.stage;
                const timeLineEntry = initiative.timeline.find((entry) => entry.stage === stageKey);

                if (timeLineEntry && timeLineEntry.date && timeLineEntry.date.actual) {
                  initiativeDate = new Date(timeLineEntry.date.actual);
                }
                if (!(initiativeDate && initiativeDate.getTime() >= fromDate.getTime() && initiativeDate.getTime() <= toDate.getTime())) {
                  filterPass = false;
                }
              }
              break;
            case 'L1 planned date':
            case 'L2 planned date':
            case 'L3 planned date':
            case 'L4 planned date':
            case 'L5 planned date':
            case 'L0 actual date':
            case 'L1 actual date':
            case 'L2 actual date':
            case 'L3 actual date':
            case 'L4 actual date':
            case 'L5 actual date':
            case 'L1 Original Planned Date':
            case 'L2 Original Planned Date':
            case 'L3 Original Planned Date':
            case 'L4 Original Planned Date':
            case 'L5 Original Planned Date':
              if (!filterTimelineDates(key, value, initiative)) {
                filterPass = false;
              }
              break;
            case 'Sub . for L1 approv . date':
            case 'Sub . for L2 approv . date':
            case 'Sub . for L3 approv . date':
            case 'Sub . for L4 approv . date':
            case 'Sub . for L5 approv . date':
            case 'Sub . back to L3 approv . date':
              if (!filterApprovalDates(key, value, initiative, initiativesWithAction)) {
                filterPass = false;
              }
              break;
            case 'Planned value last update date (calendarized impact)':
            case 'Actual value last update date (calendarized impact)':
              if (!filterImpactDate(key, value, initiative)) {
                filterPass = false;
              }
              break;
            case 'initiativeOwnerInfo':
            case 'initiativeOwnerBackupInfo':
            case 'workstreamSponsorInfo':
            case 'workstreamLeadInfo':
            case 'AllowOnlyTo':
              // The `handleArrayOfObjects` helper now needs the 'value' array.
              if (!handleArrayOfObjects(initiative[key], value)) {
                filterPass = false;
              }
              break;
            case 'workstreamSponsorWorkstream':
              if (!(
                initiative[key] &&
                Array.isArray(initiative[key]) &&
                initiative[key].some(
                  (item) => item && item.workstreamName && value.includes(item.workstreamName)
                )
              )) {
                return false;
              }
              break;
            case 'workstreamLeadSubworkstream':
              if (!(
                initiative[key] &&
                Array.isArray(initiative[key]) &&
                initiative[key].some(
                  (item) => item && item.subworkstreamName && value.includes(item.subworkstreamName)
                )
              )) {
                filterPass = false;
              }
              break;
            case 'crossFunctionalSupportWorkstreamInfo':
              const isNotSetFiltered = Array.isArray(value) && value.includes('<not set>');
              const info = initiative.crossFunctionalSupportWorkstreamInfo?.[0]?.subworkstreamName;
              if (isNotSetFiltered && !info) {
                filterPass = true;
              } else if (info && info.length > 0) {
                if (!value.includes(info)) {
                  filterPass = false;
                }
              } else {
                filterPass = false;
              }
              break;
            case 'strategyInfo':
              const isNotSetStrategy = Array.isArray(value) && value.includes('<not set>');
              const strategies = initiative.strategyInfo?.map(s => s.strategyName) || [];

              if (isNotSetStrategy && strategies.length === 0) {
                filterPass = true;
              } else if (strategies.length > 0) {
                if (!strategies.some(strategy => value.includes(strategy))) {
                  filterPass = false;
                }
              } else {
                filterPass = false;
              }
              break;
            case 'Type': {
              const initiativeType = initiative?.type;

              const hasTypeSelected = value.includes(initiativeType);
              const hasNotSetSelected = value.includes('<not set>') && !initiativeType;

              if (!hasTypeSelected && !hasNotSetSelected) {
                filterPass = false;
              }
              break;
            }
            case 'sectorInfo': {
              const isNotSectorSetFiltered = Array.isArray(value) && value.includes('<not set>');
              const sectorNames = initiative.sectorAccounts?.map(item => {
                const sectorObj = subsectorData?.find(s => s._id === item?.sector);
                return sectorObj?.sector?.sectorName;
              })
                .filter(Boolean);
              if (sectorNames?.length === 0) {
                if (!isNotSectorSetFiltered) {
                  filterPass = false;
                }
              } else {
                if (
                  Array.isArray(value) &&
                  !value.includes('<not set>') &&
                  !sectorNames?.some(name => value.includes(name))
                ) {
                  filterPass = false;
                }
              }
              break;
            }
            case 'subsectorInfo': {
              const isNotSubsectorSetFiltered =
                Array.isArray(value) && value.includes('<not set>');
              const subsectorNames = initiative?.sectorAccounts?.map(item => {
                // Find the sector object by ID
                const sectorObj = subsectorData?.find(s => s._id === item?.sector);
                return sectorObj?.subsectorName;
              })
                .filter(Boolean);
              if (subsectorNames?.length === 0) {
                // No subsector set
                if (!isNotSubsectorSetFiltered) {
                  filterPass = false;
                }
              } else {
                // Subsector exists → check match
                if (
                  Array.isArray(value) &&
                  !value.includes('<not set>') &&
                  !subsectorNames?.some(name => value.includes(name))
                ) {
                  filterPass = false;
                }
              }
              break;
            }
            case 'stageSimplified':
              if (!value.includes(determineStage(initiative))) {
                filterPass = false;
              }
              break;
            case 'stage':
              if (!value.includes(initiative.stage)) {
                filterPass = false;
              }
              break;
            case 'Description':
              if (!value.includes(initiative.description)) {
                filterPass = false;
              }
              break;
            case 'Workstream lead pending approval':
              let isApprovedStatusMatch = false;
              if (value.includes('Pending') && pendingInitiativeIds.has(initiative._id)) {
                isApprovedStatusMatch = true;
              }
              if (value.includes('Approved') && approvedInitiativeIds.has(initiative._id)) {
                isApprovedStatusMatch = true;
              }
              if (value.includes('<not set>') && notSetInitiativeIds.has(initiative._id)) {
                isApprovedStatusMatch = true;
              }
              if (!isApprovedStatusMatch) {
                filterPass = false;
              }
              break;
            case 'Workstream sponsor pending approval':
              let isSponsorApprovedStatusMatch = false;
              if (value.includes('Pending') && pendingSponsorIds.has(initiative._id)) {
                isSponsorApprovedStatusMatch = true;
              }
              if (value.includes('Approved') && approvedSponsorIds.has(initiative._id)) {
                isSponsorApprovedStatusMatch = true;
              }
              if (value.includes('<not set>') && notSetSponsorIds.has(initiative._id)) {
                isSponsorApprovedStatusMatch = true;
              }
              if (!isSponsorApprovedStatusMatch) {
                filterPass = false;
              }
              break;
            case 'TO finance pending approval':
              let isTOApprovedStatusMatch = false;
              if (value.includes('Pending') && pendingTOIds.has(initiative._id)) {
                isTOApprovedStatusMatch = true;
              }
              if (value.includes('Approved') && approvedTOIds.has(initiative._id)) {
                isTOApprovedStatusMatch = true;
              }
              if (value.includes('<not set>') && notSetTOIds.has(initiative._id)) {
                isTOApprovedStatusMatch = true;
              }
              if (!isTOApprovedStatusMatch) {
                filterPass = false;
              }
              break;
            case 'Strategy analyst pending approval':
              let isAnalystApprovedStatusMatch = false;
              if (value.includes('Pending') && pendingAnalystIds.has(initiative._id)) {
                isAnalystApprovedStatusMatch = true;
              }
              if (value.includes('Approved') && approvedAnalystIds.has(initiative._id)) {
                isAnalystApprovedStatusMatch = true;
              }
              if (value.includes('<not set>') && notSetAnalystIds.has(initiative._id)) {
                isAnalystApprovedStatusMatch = true;
              }
              if (!isAnalystApprovedStatusMatch) {
                filterPass = false;
              }
              break;
            case 'Workstream lead pending approvers': {
              const pendingInit = workstreamLeadPendingMap.get(initiative._id);
              let approverName = "<not set>";

              if (pendingInit?.workstreamLeadInfo?.[0]) {
                approverName = `${pendingInit.workstreamLeadInfo[0].firstName} ${pendingInit.workstreamLeadInfo[0].lastName}`;
              }

              if (!value.includes(approverName)) {
                filterPass = false;
              }
              break;
            }

            case 'Workstream sponsor pending approvers': {
              const pendingInit = sponsorPendingMap.get(initiative._id);
              let approverName = "<not set>";

              if (pendingInit?.workstreamSponsorInfo?.[0]) {
                approverName = `${pendingInit.workstreamSponsorInfo[0].firstName} ${pendingInit.workstreamSponsorInfo[0].lastName}`;
              }

              if (!value.includes(approverName)) {
                filterPass = false;
              }
              break;
            }

            case 'TO finance pending approvers': {
              const pendingInit = toFinancePendingMap.get(initiative._id);
              let approverName = "<not set>";

              if (pendingInit?.toFinanceInfo?.[0]) {
                approverName = `${pendingInit.toFinanceInfo[0].firstName} ${pendingInit.toFinanceInfo[0].lastName}`;
              }

              if (!value.includes(approverName)) {
                filterPass = false;
              }
              break;
            }

            case 'Strategy analyst pending approvers': {
              const pendingInit = strategyAnalystPendingMap.get(initiative._id);
              let approverName = "<not set>";

              if (pendingInit?.strategyAnalystInfo?.[0]) {
                approverName = `${pendingInit.strategyAnalystInfo[0].firstName} ${pendingInit.strategyAnalystInfo[0].lastName}`;
              }

              if (!value.includes(approverName)) {
                filterPass = false;
              }
              break;
            }
            case 'Net recurring benefit range':
              if (!value.some(range => initiativesNetRecurringBenefits[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Future implementation costs range':
              if (!value.some(range => initiativesImplementationCostRange[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Pipeline weekly variance (L0+)':
              if (!value.some(range => initiativesL0Group[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Pipeline weekly variance (L1+)':
              if (!value.some(range => initiativesL1Group[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Pipeline weekly variance (L2+)':
              if (!value.some(range => initiativesL2Group[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Pipeline weekly variance (L3+)':
              if (!value.some(range => initiativesL3Group[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Pipeline weekly variance (L4+)':
              if (!value.some(range => initiativesL4Group[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case '(LW) Weekly status':
              if (!value.some(range => initiativesLastWeeklyStatus[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case '(LW) Stage':
              if (!value.some(range => lastWeekStage[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case '(LW) Planning summary': // This is for initiative LW Planning Summary
              if (!value.some(range => initiativesLWPlanningSummary[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Change in net recurring benefits (binned, current vs last week, latest estimate, annualized)':
              if (!value.some(range => initiativesCurrentVsLastWeek[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Change in One Time Benefit (binned, current vs last week, latest estimate, annualized)':
              if (!value.some(range => initiativesOneTimeBenefitCurrentVsLastweek[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Change in implementation costs (binned, latest estimate, annualized)':
              if (!value.some(range => initiativesImplementation[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'FY 2023/24 actual impact update check':
              if (!value.some(range => updateCheck[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'FY 2024/25 actual impact update check':
              if (!value.some(range => updateCheck2025[range]?.some(item => item._id === initiative._id))) {
                filterPass = false;
              }
              break;
            case 'Number of attachments':
              const attachmentCount = initiative?.attachmentCount || 0;

              if (Array.isArray(value)) {
                if (!value.includes(attachmentCount)) {
                  filterPass = false;
                }
              }
              break;

            case 'frequencyAndDirection':
            case 'impactNumber':
            case 'typeOfImpact':
            case 'Whose P&L does this impact?':
            case 'sector':
            case 'subsector':
              if (!filterImpactAttributes(key, value, initiative, workstreamsData, subsectorData)) {
                filterPass = false;
              }
              break;
            case 'TypeOfKPI':
            case 'UniversalKPI':
            case 'unitOfMeasurement':
            case 'aggregationMode':
            case 'kpiSector':
            case 'kpiSubsector':
              if (!filterKPIAttributes(key, value, initiative, subsectorData)) {
                filterPass = false;
              }
              break;
            default:
              // For general string/number/boolean comparisons for initiative attributes
              if (Array.isArray(initiative[key])) {
                if (!initiative[key].some((item) => value.includes(item))) {
                  filterPass = false;
                }
              } else {
                if (!value.includes(initiative[key])) { // Assuming value is an array for multi-select
                  filterPass = false;
                }
              }
              break;
          }
          if (!filterPass) {
            break; // If any filter fails, no need to check others for this initiative
          }
        }
        return filterPass; // Initiative passed all filters
      });
    }
    if (hasMilestoneAttributeFilters) {
      result = result.filter((milestone) => { // Filter from allAvailableMilestones
        for (const [filterKey, filterValue] of Object.entries(attributeFilters)) {
          if (!MILESTONE_FILTER_KEYS.includes(filterKey)) {
            continue;
          }
          if (filterValue === undefined) {
            continue;
          }
          if (Array.isArray(filterValue) && filterValue.length === 0) {
            return false;
          }
          const currentFromDate = filterValue?.fromDate?._d;
          const currentToDate = filterValue?.toDate?._d;
          let milestoneAttributeValue;

          switch (filterKey) {
            case 'Owner':
              const matchingUserIds = usersData
                .filter(user => filterValue.includes(`${user.firstName} ${user.lastName}`))
                .map(user => String(user._id));

              if (!matchingUserIds.includes(String(milestone.Owner))) {
                return false;
              }
              break;
            case 'milestoneNumber - name':
              if (!filterValue.includes(String(`${milestone.milestoneNumber} - ${milestone.name}`))) {
                return false;
              }
              break;
            case 'milestoneNumber':
              if (!filterValue.includes(String(milestone.milestoneNumber))) {
                return false;
              }
              break;
            case 'name':
              if (!filterValue.includes(String(milestone.name))) {
                return false;
              }
              break;
            case 'Milestone Status':
              milestoneAttributeValue = milestone.status;
              if (!filterValue.some((val) => String(milestoneAttributeValue).toLowerCase() === String(val).toLowerCase())) {
                return false;
              }
              break;
            case 'Work starting date':
              const milestoneStartDate = new Date(milestone.startDate);
              if (currentFromDate && currentToDate) {
                if (!(milestoneStartDate.getTime() >= currentFromDate.getTime() && milestoneStartDate.getTime() <= currentToDate.getTime())) {
                  return false;
                }
              } else if (currentFromDate) {
                if (!(milestoneStartDate.getTime() >= currentFromDate.getTime())) {
                  return false;
                }
              } else if (currentToDate) {
                if (!(milestoneStartDate.getTime() <= currentToDate.getTime())) {
                  return false;
                }
              }
              break;
            case 'Milestone completion date':
              const milestoneEndDate = new Date(milestone.endDate);

              if (currentFromDate && currentToDate) {
                if (!(milestoneEndDate.getTime() >= currentFromDate.getTime() && milestoneEndDate.getTime() <= currentToDate.getTime())) {
                  return false;
                }
              } else if (currentFromDate) {
                if (!(milestoneEndDate.getTime() >= currentFromDate.getTime())) {
                  return false;
                }
              } else if (currentToDate) {
                if (!(milestoneEndDate.getTime() <= currentToDate.getTime())) {
                  return false;
                }
              }
              break;
            case 'Updated On':
              const historyEntriesUpdated = filtered.flatMap((init) => init.history || []); // Use currentInitiatives for history lookup
              const exactUpdatedHistories = historyEntriesUpdated
                .filter(
                  (entry) =>
                    entry.action ===
                    `Milestone with Number ${milestone.milestoneNumber} has been updated.`
                )
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

              const latestEntryUpdated = exactUpdatedHistories[0];

              if (!latestEntryUpdated) return false;

              const updatedOnDate = new Date(latestEntryUpdated.timestamp);
              const matchUpdated = latestEntryUpdated.action.match(/Number (\d+)/);
              if (matchUpdated && milestone.milestoneNumber === matchUpdated[1]) {
                if (currentFromDate && currentToDate) {
                  if (!(updatedOnDate.getTime() >= currentFromDate.getTime() && updatedOnDate.getTime() <= currentToDate.getTime())) {
                    return false;
                  }
                } else if (currentFromDate) {
                  if (!(updatedOnDate.getTime() >= currentFromDate.getTime())) {
                    return false;
                  }
                } else if (currentToDate) {
                  if (!(updatedOnDate.getTime() <= currentToDate.getTime())) {
                    return false;
                  }
                }
              } else {
                return false;
              }
              break;
            case 'Created On':
              // 1. Find all history entries related to this milestone's creation
              const createdHistoryEntries = filtered
                .flatMap((init) => init.history || [])
                .filter((entry) =>
                  entry.action?.includes(`Milestone #${milestone.name} created`)
                )
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

              const latestEntry = createdHistoryEntries[0];

              // 2. If no creation history is found, this milestone doesn't match the filter.
              if (!latestEntry) {
                return false;
              }

              const createdOnDate = new Date(latestEntry.timestamp);

              // 3. Perform date filtering based on the found timestamp
              const hasFromDate = currentFromDate && createdOnDate.getTime() >= currentFromDate.getTime();
              const hasToDate = currentToDate && createdOnDate.getTime() <= currentToDate.getTime();

              // 4. Check if the milestone falls within the date range
              if (currentFromDate && currentToDate) {
                return hasFromDate && hasToDate;
              } else if (currentFromDate) {
                return hasFromDate;
              } else if (currentToDate) {
                return hasToDate;
              }
              break;
            case 'Created by':
              const creationHistoryEntries = filtered.flatMap((init) => init.history || []);
              const matchingCreationEntry = creationHistoryEntries
                .filter((entry) =>
                  entry.action?.includes(`Milestone #${milestone.name} created`))
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

              const createdById = matchingCreationEntry?.user;
              const creatorUser = usersData?.find((u) => u._id === createdById);
              // Determine the exact value this milestone will be filtered by
              const milestoneCreatedValue = creatorUser
                ? `${creatorUser.firstName || ''} ${creatorUser.lastName || ''}`.trim()
                : '<not set>'; // If no creator, assign '<not set>'

              if (Array.isArray(filterValue) && filterValue.length > 0) {
                // If the filter has selections, check if the milestone's value matches any selected option
                return filterValue.some((selectedOption) =>
                  String(milestoneCreatedValue).toLowerCase() === String(selectedOption).toLowerCase()
                );
              }
              break;
            case 'Updated by':
              // Determine the actual 'Updated by' value for this specific milestone
              // Similar efficiency note as above for 'Created by' history lookup.
              const updateHistoryEntries = filtered.flatMap((init) => init.history || []);

              const matchingUpdateEntry = updateHistoryEntries
                .filter((entry) =>
                  entry.action?.includes(`Milestone with Number ${milestone.milestoneNumber} has been updated.`))
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

              const updatedById = matchingUpdateEntry?.user;
              const updaterUser = usersData?.find((u) => u._id === updatedById);
              // Determine the exact value this milestone will be filtered by
              const milestoneUpdatedValue = updaterUser
                ? `${updaterUser.firstName || ''} ${updaterUser.lastName || ''}`.trim()
                : '<not set>'; // If no updater, assign '<not set>'

              if (Array.isArray(filterValue) && filterValue.length > 0) {
                // If the filter has selections, check if the milestone's value matches any selected option
                return filterValue.some((selectedOption) =>
                  String(milestoneUpdatedValue).toLowerCase() === String(selectedOption).toLowerCase()
                );
              }
              break;
            case 'Milestone Planning summary':
              // This needs the parent initiative for context to determine its planning summary
              if (!filterMilestoneByPlanningSummary(milestone, filtered, filterValue)) {
                return false;
              }
              break;
            case 'Planning summary (simplified)':
              if (!filterMilestoneByPlanningSummarySimpified(milestone, filterValue, filtered)) {
                return false;
              }
              break;
            case '(LW) Planning summary':
              // This also needs the parent initiative for context
              if (!filterMilestonesByLWPlanningSummary(milestone, filtered, filterValue)) {
                return false;
              }
              break;
            case 'Change in Milestone completion date (binned, current vs last week)':
              const milestonesEndDate = milestone.endDate ? new Date(milestone.endDate) : null;
              const currentDate = new Date();
              const selectedBins = filterValue; // use `filterValue` for consistency with other cases

              if (!milestonesEndDate) {
                if (!selectedBins.includes('<not set>')) return false;
                break;
              }

              if (milestone.status === 'Completed') {
                if (!selectedBins.includes('Unchanged')) return false;
                break;
              }

              const timeDifference = milestonesEndDate.getTime() - currentDate.getTime();
              const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24)); // Convert ms to days

              if (daysDifference < 0) {
                if (!selectedBins.includes('Accelerated')) return false;
              } else {
                if (selectedBins.includes('Unchanged') && daysDifference === 0) break;
                if (selectedBins.includes('Delayed (1 to 7 days)') && daysDifference >= 1 && daysDifference <= 7) break;
                if (selectedBins.includes('Delayed (8 to 14 days)') && daysDifference >= 8 && daysDifference <= 14) break;
                if (selectedBins.includes('Delayed (15 to 30 days)') && daysDifference >= 15 && daysDifference <= 30) break;
                if (selectedBins.includes('Delayed (more than 30 days)') && daysDifference > 30) break;
                return false;
              }
              break;
            default:
              milestoneAttributeValue = milestone[filterKey];
              if (milestoneAttributeValue === undefined || filterValue === undefined) {
                break;
              }
              if (Array.isArray(filterValue)) {
                if (!filterValue.some((val) => String(milestoneAttributeValue).toLowerCase() === String(val).toLowerCase())) {
                  return false;
                }
              } else {
                if (!(String(milestoneAttributeValue).toLowerCase() === String(filterValue).toLowerCase())) {
                  return false;
                }
              }
              break;
          }
        }
        return true; // Milestone passed all its specific filters
      });

    }
    if (hasActionItemAttributeFilters) {
      actionResult = actionResult.filter((action) => { // Filter from allAvailableActionItem
        for (const [filterKey, filterValues] of Object.entries(attributeFilters)) {
          if (!ACTION_ITEM_FILTER_KEYS.includes(filterKey)) {
            continue;
          }

          if (filterValues === undefined) {
            continue;
          }
          if (Array.isArray(filterValues) && filterValues.length === 0) {
            return false;
          }
          switch (filterKey) {
            case 'planningSummary':
              if (!filterValues.includes(determinePlanningSummary(action))) {
                return false;
              }
              break;
            case 'owner':
              // Assuming action.owner exists and has firstName/lastName
              const ownerName = `${action.owner?.firstName || ''} ${action.owner?.lastName || ''}`;
              if (!filterValues.some((val) => String(ownerName).toLowerCase() === String(val).toLowerCase())) {
                return false;
              }
              break;
            case 'collaborator': {
              const collaborators = Array.isArray(action.collaborator)
                ? action.collaborator
                : [];
              const collaboratorNames = collaborators.map((col) => {
                const full = `${col?.firstName || ''} ${col?.lastName || ''}`.trim();
                return full;
              });
              const matchFound = collaboratorNames.some((name) =>
                filterValues.some(
                  (val) => String(name).toLowerCase() === String(val).toLowerCase()
                )
              );
              if (!matchFound) return false;
              break;
            }
            case 'Accountable Workstream': {
              // Get workstream from owner
              const ownerWorkstreamName = action.owner?.workstream?.workstreamName ||
                action.owner?.subworkstream?.workstream?.workstreamName;

              // Get workstream from collaborators
              const collaboratorWorkstreamNames = action.collaborator
                ?.filter(collab => collab) // Filter out null/undefined
                .map(collab => collab?.workstream?.workstreamName || collab?.subworkstream?.workstream?.workstreamName)
                .filter(name => name && typeof name === 'string' && name.trim() !== '') || [];

              // Ensure safe string handling for owner workstream
              const ownerWorkstreamNameFormatted =
                typeof ownerWorkstreamName === 'string' && ownerWorkstreamName.trim() !== ''
                  ? ownerWorkstreamName.trim()
                  : null;

              // Check if either owner workstream or any collaborator workstream matches filter values
              let matchesFilter = false;

              // Check owner workstream
              if (ownerWorkstreamNameFormatted) {
                matchesFilter = filterValues.some(
                  (val) => ownerWorkstreamNameFormatted.toLowerCase() === String(val).toLowerCase()
                );
              }

              // Check collaborator workstreams if not already matched
              if (!matchesFilter && collaboratorWorkstreamNames.length > 0) {
                matchesFilter = collaboratorWorkstreamNames.some(collabWorkstream =>
                  filterValues.some(
                    (val) => collabWorkstream.toLowerCase() === String(val).toLowerCase()
                  )
                );
              }

              if (!matchesFilter) {
                return false;
              }
              break;
            }
            case 'Accountable subworkstream': {
              // Get subworkstream from owner
              const ownerSubworkstreamName = action.owner?.subworkstream?.subworkstreamName;

              // Get subworkstream from collaborators
              const collaboratorSubworkstreamNames = action.collaborator
                ?.filter(collab => collab) // Filter out null/undefined
                .map(collab => collab?.subworkstream?.subworkstreamName)
                .filter(name => name && typeof name === 'string' && name.trim() !== '') || [];

              // Format owner subworkstream name
              const ownerSubworkstreamNameFormatted =
                typeof ownerSubworkstreamName === 'string' && ownerSubworkstreamName.trim() !== ''
                  ? ownerSubworkstreamName.trim()
                  : null;
              // Check if either owner subworkstream or any collaborator subworkstream matches filter values
              let matchesFilter = false;
              // Check owner subworkstream
              if (ownerSubworkstreamNameFormatted) {
                matchesFilter = filterValues.some(
                  (val) => ownerSubworkstreamNameFormatted.toLowerCase() === String(val).toLowerCase()
                );
              }
              // Check collaborator subworkstreams if not already matched
              if (!matchesFilter && collaboratorSubworkstreamNames.length > 0) {
                matchesFilter = collaboratorSubworkstreamNames.some(collabSubworkstream =>
                  filterValues.some(
                    (val) => collabSubworkstream.toLowerCase() === String(val).toLowerCase()
                  )
                );
              }

              if (!matchesFilter) {
                return false;
              }
              break;
            }
            case 'Created on':
              // Wrap in a block to create a new scope for `const` declarations
              {
                const createdFilterFromDate = filterValues?.fromDate?._d;
                const createdFilterToDate = filterValues?.toDate?._d;
                const createdDate = new Date(action.created);

                if (createdFilterFromDate && createdFilterToDate) {
                  if (!(createdDate.getTime() >= createdFilterFromDate.getTime() && createdDate.getTime() <= createdFilterToDate.getTime())) {
                    return false;
                  }
                } else if (createdFilterFromDate) {
                  if (!(createdDate.getTime() >= createdFilterFromDate.getTime())) {
                    return false;
                  }
                } else if (createdFilterToDate) {
                  if (!(createdDate.getTime() <= createdFilterToDate.getTime())) {
                    return false;
                  }
                }
              }
              break;
            case 'Updated on':
              // Wrap in a block to create a new scope for `const` declarations
              {
                const updateLog = action.activity?.find(ev =>
                  ev.event?.includes(`Workstream action ${action.actionItemNumber} is updated`)
                );
                if (!updateLog) return false;

                const updatedActionDate = new Date(updateLog.eventDate);
                // Use distinct variable names for 'Updated on' case
                const updatedFilterFromDate = filterValues?.fromDate?._d;
                const updatedFilterToDate = filterValues?.toDate?._d;

                if (updatedFilterFromDate && updatedFilterToDate) {
                  if (!(updatedActionDate.getTime() >= updatedFilterFromDate.getTime() &&
                    updatedActionDate.getTime() <= updatedFilterToDate.getTime())) {
                    return false;
                  }
                } else if (updatedFilterFromDate) {
                  if (!(updatedActionDate.getTime() >= updatedFilterFromDate.getTime())) {
                    return false;
                  }
                } else if (updatedFilterToDate) {
                  if (!(updatedActionDate.getTime() <= updatedFilterToDate.getTime())) {
                    return false;
                  }
                }
              }
              break;
            case 'createdBy':
              const filterCreatedByNames = filterValues;
              const createdByFullName = `${action.createdBy?.firstName || ''} ${action.createdBy?.lastName || ''}`.trim();

              // If createdBy information is missing, and '<not set>' is selected as a filter
              if (!action.createdBy && filterCreatedByNames.some(name => String(name).toLowerCase() === '<not set>')) {
                break; // Match found, so don't return false
              }

              // If createdBy information exists or '<not set>' is not selected, proceed with name comparison
              if (action.createdBy && !filterCreatedByNames.some(
                (name) => String(createdByFullName).toLowerCase() === String(name).toLowerCase()
              )) {
                return false;
              }
              break;

            case 'Updated By':
              const latestUpdate = (action.activity || [])
                .filter(ev => ev.event?.includes('is updated'))
                .sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate))[0];

              let updaterName;
              if (!latestUpdate || !latestUpdate.owner) {
                updaterName = '<not set>'; // If no latest update or owner, categorize as '<not set>'
              } else {
                updaterName = `${latestUpdate.owner.firstName || ''} ${latestUpdate.owner.lastName || ''}`.trim();
              }

              if (Array.isArray(filterValues)) {
                // Check if any of the filter values match the updaterName (which could be '<not set>')
                if (!filterValues.some(val => updaterName.toLowerCase() === String(val).toLowerCase())) {
                  return false;
                }
              }
              break;
            default:
              const actionValue = action[filterKey];
              if (Array.isArray(filterValues)) {
                if (!filterValues.some((val) => String(actionValue).toLowerCase() === String(val).toLowerCase())) {
                  return false;
                }
              } else {
                if (!(String(actionValue).toLowerCase() === String(filterValues).toLowerCase())) {
                  return false;
                }
              }
              break;
          }
        }
        return true; // Action item passed all its specific filters
      });

    }
    if (milestoneCurrentFilter || hasMilestoneAttributeFilters) {
      filtered = filtered.filter((init) =>
        init.milestones?.some((m) => result.some((fm) => fm._id === m._id))
      );
    }
    result = result.filter((milestone) =>
      filtered.some((init) => init.milestones?.some((m) => m._id === milestone._id))
    );
    const currentSortField = sortField || 'initiativeNumber';
    const currentSortOrder = sortOrder || 'desc';

    // Sort initiatives
    if (currentSortField && currentSortField !== 'None') {
      const filteredCopy = [...filtered];
      applyInitiativeSort(filteredCopy, currentSortField, currentSortOrder, getNameToSort, workstreamsData, subsectorData);
      filtered = filteredCopy;
    }
    // Apply sorting to Milestones
    if (sortField) {
      const historyEntries = filtered.flatMap((h) => h.history || []);
      applyMilestoneSort(result, sortField, sortOrder, historyEntries, usersData);
    }
    // Apply sorting to Workstream Actions
    if (sortField) {
      applyWorkstreamActionSort(actionResult, sortField, sortOrder, workstreamData);
    }
    if (colorByAttribute) {
      handleColoring(
        colorByAttribute,
        filtered,
        result,
        actionResult,
        setInitiativeColors,
        setMilestoneColors,
        setActionItemColors,
        usersData,
        getRandomColor,
        InitiativePlanningSummary,
        checkToFinanceApprovalStatus,
        getNetRecurringBenefitCategory,
        checkWorkstreamLeadApprovalStatus,
        checkWorkstreamSponsorApprovalStatus,
        checkStrategyAnalystApprovalStatus,
        workstreamData,
        determineStage,
        subsectorData
      );
    }
    if (selectedImpactGroup) {
      const grouped = groupImpactsByPeriod(filtered, selectedImpactGroup, selectedDateRange, determineResult, periodCurrentFilter);
      setGroupedImpact(grouped);
    }
    const selected =
      selectedGroup !== 'None'
        ? selectedGroup
        : selectedHistoryGroup && selectedHistoryGroup.length >= 0
          ? selectedHistoryGroup
          : null;
    let grouped = {};
    if (selected) {
      const { groupedData, totals } = groupInitiativesBy(
        filtered,
        selected,
        determineStage,
        checkToFinanceApprovalStatus,
        checkWorkstreamLeadApprovalStatus,
        checkWorkstreamSponsorApprovalStatus,
        getNetRecurringBenefitCategory,
        InitiativePlanningSummary,
        planningSummarySimplified,
        L0ActualDate,
        L1ActualDate,
        L2ActualDate,
        L2PlanDate,
        L3ActualDate,
        L3PlanDate,
        L4ActualDate,
        L4PlanDate,
        L5ActualDate,
        L5PlanDate,
        L1OriginalPlanDate,
        L2OriginalPlanDate,
        L3OriginalPlanDate,
        L4OriginalPlanDate,
        L5OriginalPlanDate,
        LLatestActualDate,
        SubmittedForL1PlanDate,
        SubmittedForL2PlanDate,
        SubmittedForL3PlanDate,
        SubmittedForL4PlanDate,
        SubmittedForL5PlanDate,
        getNetRecurringBenefitChangeCategoryL0Plus,
        getNetRecurringBenefitChangeCategoryL1Plus,
        getNetRecurringBenefitChangeCategoryL2Plus,
        getNetRecurringBenefitChangeCategoryL3Plus,
        getNetRecurringBenefitChangeCategoryL4Plus,
        getComplianceStatus,
        groupInitiativesByYearActual,
        sortInitiativesByActuals,
        checkStrategyAnalystApprovalStatus,
        usersData,
        workstreamData,
        historyData,
        selectedRange,
        sortInitiativesByActualImpact,
        calculateTotalNetRecurringBenefit,
        sumVisible,
        subsectorData
      );
      if (selected && selectedGroup === 'None') {
        setGroupedInitiativesByHistory(groupedData);
      }
      setGroupedInitiatives(groupedData);
      setFinalTotals(totals)
      setFilteredData([]);
    }
    if (selectedGroup) {
      const grouped = groupWorkstreamActionsBy(
        actionResult,
        selectedGroup,
        usersData,
        determinePlanningSummary
      );
      setGroupedActionItems(grouped);
    } else {
      setGroupedInitiatives({});
      setFilteredData(filtered);
    }
    if (selectedGroup) {
      const grouped = groupMilestonesBy(
        result,
        selectedGroup,
        usersData,
        filtered,
        determineStage,
        InitiativePlanningSummary,
        planningSummarySimplified
      );
      setGroupedMilestones(grouped);
    }
    setFilteredData(filtered);
    setFilteredMilestones(result);
    setFilteredActionItems(actionResult);
    setSumResults(determineResult);
    setFilteredImpact(determineImpactOrKPI);
  };
  const grandTotal = Object.values(finalTotals).reduce((sum, count) => sum + count, 0);
  const { milestones } = useMilestoneData(filteredData, currentUser);
  const {
    searchResults,
    searchInputValue,
    handleSearchInputChange,
    handleSearchSelect,
    isAddMilestoneModalOpen,
    setIsAddMilestoneModalOpen,
    selectedMilestone,
    selectedInitiative,
    isWorkstreamActionModalOpen,
    setIsWorkstreamActionModalOpen,
    selectedWorkstreamAction,
  } = useSearch(isSearchVisible, initiativesData, workstreamActionsData);
  const fetchWorkbookData = async () => {
    setLoadingWorkbook(true);
    try {
      setWorkbookData([]);
    } catch (error) {
      console.error('Error fetching workbook data:', error);
      setWorkbookData([]);
    } finally {
      setLoadingWorkbook(false);
    }
  };
  // Effect to handle URL search parameters for Workbook and Dashboard visibility.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isSectionWorkbook = searchParams.get('section') === 'workbook';
    const isSectionDashboard = searchParams.get('section') === 'dashboard';
    setIsWorkbookVisible(isSectionWorkbook);
    setIsDashboardVisible(isSectionDashboard);
    if (isSectionWorkbook) {
      fetchWorkbookData();
    } else if (isSectionDashboard) {
      fetchWorkbookData();
    } else {
      setWorkbookData(null); // Clear workbook data if neither section is active.
    }
  }, [location]);

  // This effect to clear search input/results when hiding search is now
  // largely handled internally by useSearch, but kept as a general cleanup.
  // useEffect(() => {
  //   if (!isSearchVisible) {
  //     setSearchInputValue('');
  //     setSearchResults([]);
  //   }
  // }, [isSearchVisible, setSearchInputValue, setSearchResults]);


  const handleWorkbookClick = () => {
    const searchParams = new URLSearchParams(location.search);
    // Toggle 'workbook' section in URL.
    if (searchParams.get('section') === 'workbook') {
      searchParams.delete('section');
    } else {
      searchParams.set('section', 'workbook');
    }
    history.push({ pathname: '/', search: searchParams.toString() });
  };

  const handleDashboardClick = () => {
    const searchParams = new URLSearchParams(location.search);
    // Toggle 'dashboard' section in URL.
    if (searchParams.get('section') === 'dashboard') {
      searchParams.delete('section');
    } else {
      searchParams.set('section', 'dashboard');
    }
    history.push({ pathname: '/', search: searchParams.toString() });
  };

  const handleHeaderClick = (route) => () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('section'); // Clear section parameter when navigating to a new route.
    history.push({ pathname: route, search: searchParams.toString() });
    setDrawerVisible(false); // Close drawer on navigation from mobile menu.
  };

  // Dropdown content for user profile.
  const profileDropdown = (
    <div className="profileDropdown whiteBox shadow" style={{ minWidth: '200px' }}>
      <div className="pad15" onClick={handleHeaderClick('/profile')} style={{ cursor: 'pointer' }}>
        <Avatar size="medium" className="last bg-blue-500" style={{ float: 'left' }}>
          {currentUser?.firstName && currentUser?.firstName.charAt(0).toUpperCase()}
        </Avatar>
        <div className="info">
          <p className="strong">
            {capitalizeFirstLetter(currentUser?.firstName)}{' '}
            {capitalizeFirstLetter(currentUser?.lastName)}
          </p>
          <p>{currentUser?.email}</p>
        </div>
      </div>
      <div className="line" />
      <Menu>
        <Menu.Item
          icon={<SettingOutlined />}
          key={uniqueId()}
          onClick={handleHeaderClick('/profile')}
        >
          My Profile
        </Menu.Item>
      </Menu>
      <div className="line" />
      <Menu>
        <Menu.Item
          icon={<LogoutOutlined />}
          key={uniqueId()}
          onClick={handleHeaderClick('/logout')}
        >
          Sign Out
        </Menu.Item>
      </Menu>
    </div>
  );
  const handleHistoryScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if the user has scrolled to the bottom of the popover content
    // Add a small buffer (e.g., 1px) to ensure it triggers correctly
    if (scrollHeight - scrollTop - clientHeight < 1 && hasMore && !loadingHistory) {
      fetchHistory(); // Load more data when at the bottom
    }
  }, [hasMore, loadingHistory, fetchHistory]);
  // Popover content for audit history.
  const historyPopover = (
    <div
      ref={historyPopoverContentRef}
      onScroll={handleHistoryScroll}
      style={{ width: '300px', maxHeight: '300px', overflowY: 'auto' }}
    >
      <h4>Recent History</h4>
      {/* Check if auditHistory is an array and has items */}
      {Array.isArray(auditHistory) && auditHistory.length > 0 ? (
        auditHistory.map((item) => (
          <div
            key={item._id}
            style={{ marginBottom: '10px', padding: '10px', borderBottom: '1px solid #ddd' }}
          >
            <p>
              <strong>Event:</strong> {item.event}
            </p>
            <p>
              <strong>User:</strong> {item.user?.firstName || 'Unknown'} {item.user?.lastName || ''}
            </p>
            <p>
              <strong>Date:</strong> {new Date(item.created).toLocaleString()}
            </p>
          </div>
        ))
      ) : (
        <div>No audit history available</div>
      )}
      {loadingHistory && <Spin style={{ display: 'block', margin: '10px auto' }} />}
      {!hasMore && !loadingHistory && Array.isArray(auditHistory) && auditHistory.length > 0 && (
        <div style={{ textAlign: 'center', padding: '10px' }}>End of history</div>
      )}
    </div>
  );
  const handleDownloadClick = useDownloadData(usersData, initiativesData, workstreamData, workstreamsData, workstreamActionsData, historyData, selectedRange)

  const handleAddNewInitiative = () => {
    history.push('/initiative');
    setDrawerVisible(false); // Close drawer after navigation.
  };

  const handleAddNewWorkstreamAction = () => {
    history.push('/workstreamaction');
    setDrawerVisible(false); // Close drawer after navigation.
  };

  // Menu for "Add New" dropdown.
  const addMenu = (
    <Menu style={{ background: '#253271', color: 'white' }}>
      <Menu.Item key="add-initiative" onClick={handleAddNewInitiative} style={{ color: 'white' }}>
        Add New Initiative
      </Menu.Item>
      <Menu.Item
        key="add-workstream-action"
        onClick={handleAddNewWorkstreamAction}
        style={{ color: 'white' }}
      >
        Add New Workstream Action
      </Menu.Item>
    </Menu>
  );

  // Mobile menu content for the Drawer.
  const mobileMenu = (
    <Menu mode="vertical" style={{ borderRight: 0 }}>
      <Menu.Item key="home" onClick={handleHeaderClick('/')}>
        HOME
      </Menu.Item>
      {currentUser.role?.name !== 'Guest' && (
        <>
          <Menu.Item key="dashboard" onClick={handleDashboardClick}>
            Dashboard
          </Menu.Item>
          <Menu.Item key="workbook" onClick={handleWorkbookClick}>
            WORKBOOK
          </Menu.Item>
        </>
      )}
      <Menu.Item key="help-center" onClick={handleHeaderClick('/help-center')}>
        HELP CENTER
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="add-initiative-mobile" onClick={handleAddNewInitiative}>
        <PlusOutlined /> Add New Initiative
      </Menu.Item>
      <Menu.Item key="add-workstream-action-mobile" onClick={handleAddNewWorkstreamAction}>
        <PlusOutlined /> Add New Workstream Action
      </Menu.Item>
      <Menu.Item key="download-data" onClick={handleDownloadClick}>
        <DownloadOutlined /> Download Data
      </Menu.Item>
      <Menu.Item key="copy-link" onClick={handleCopyLinkClick}>
        <SendOutlined /> Copy Link
      </Menu.Item>
    </Menu>
  );

  return (
    <div
      className="headerContainer"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        height: '60px',
        background: '#f0f2f5',
        backgroundColor: '#253271',
        color: 'white',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}
    >
      <div
        className="headerNav"
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '30px',
        }}
      >
        <div
          className="logo"
          onClick={handleHeaderClick('/')}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <div className="h-56 grid grid-cols-3 gap-1 content-stretch">
            <img src={logoIcon} alt="Logo" style={{ height: '32px', borderRadius: '50%' }} />
            <img src={logoText} alt="Logo" style={{ height: '29px' }} />
          </div>
        </div>
        {currentUser.role?.name !== 'Guest' && (
          <div
            className="desktop-nav-items"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <Dropdown overlay={addMenu} trigger={['click']}>
              <PlusOutlined className="anticon-lg" />
            </Dropdown>
            <Button onClick={handleDownloadClick} loading={loadingWorkbook} icon={<DownloadOutlined
              className="anticon-lg"
            />} type='primary' style={{ backgroundColor: '#253271', border: 'none' }}>
            </Button>
            <SendOutlined className="anticon-lg" onClick={handleCopyLinkClick} />
          </div>
        )}
        <div
          className="desktop-menu-items"
          style={{ display: 'flex', alignItems: 'center', gap: '30px' }}
        >
          <div onClick={handleHeaderClick('/')} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
            HOME
          </div>
          {currentUser.role?.name !== 'Guest' && (
            <>
              <div onClick={handleDashboardClick} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Dashboard
              </div>
              <div onClick={handleWorkbookClick} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                WORKBOOK
              </div>
            </>
          )}
          <div
            onClick={handleHeaderClick('/help-center')}
            style={{ cursor: 'pointer', fontWeight: 'bold' }}
          >
            HELP CENTER
          </div>
        </div>
      </div>
      <div className="headerIcons" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        {/* Search Autocomplete Input */}
        {isSearchVisible && (
          <AutoComplete
            options={searchResults.map((result) => ({
              value: result.formatted,
            }))}
            style={{ width: '600px', marginRight: '10px' }}
            onSelect={handleSearchSelect}
            onChange={handleSearchInputChange}
            value={searchInputValue}
            // Display loading message if search data is being fetched.
            // placeholder={loadingSearchData ? "Loading search data..." : "Search..."}
            filterOption={(inputValue, option) =>
              option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
            }
          // loading={loadingSearchData} // Show Ant Design's loading spinner in the input.
          />
        )}
        {isAddMilestoneModalOpen && (
          <AddMilestoneModal
            isAddMilestoneModalOpen={isAddMilestoneModalOpen}
            setAddMilestoneModal={setIsAddMilestoneModalOpen}
            milestoneDetails={selectedMilestone} // Pass the milestone ID
            edit={true}
            initiative={selectedInitiative} // Pass the initiative ID
            onMilestoneCreate={() => {
              setIsAddMilestoneModalOpen(false);
              // You may need to reset state here if not handled by the modal
            }}
            users={usersData}
          />
        )}
        {isWorkstreamActionModalOpen && selectedWorkstreamAction && (
          <WorkstreamActionModal
            isWorkstreamActionModalOpen={isWorkstreamActionModalOpen}
            setWorkstreamActionModal={setIsWorkstreamActionModalOpen}
            workstreamActionDetails={selectedWorkstreamAction}
            users={usersData}
            addMenu={addMenu}
            handleCopyLinkClick={handleCopyLinkClick}
            onWorkstreamActionUpdate={(updatedAction) => {
              // Handle updates if needed
              console.log('Workstream action updated:', updatedAction);
            }}
          />
        )}
        {/* Search Icon to toggle search visibility */}
        <SearchOutlined
          style={{ fontSize: '18px', cursor: 'pointer' }}
          onClick={() => setIsSearchVisible(!isSearchVisible)}
        />


        {/* Audit History Popover */}
        <Popover
          content={historyPopover}
          title="Audit History"
          trigger="click"
          visible={isHistoryVisible}
          onVisibleChange={(visible) => {
            setIsHistoryVisible(visible);
            if (visible && auditHistory.length === 0) {
              fetchHistory();
            }
          }}
          placement="bottomRight" // Changed from topRight for better positioning
          overlayStyle={{ width: '300px' }}
        >
          <div
            style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              backgroundColor: '#253271',
              color: 'white',
              padding: '10px',
              borderRadius: '50%',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
            History
          </div>
        </Popover>
        {/* User Profile Dropdown */}
        <Dropdown overlay={profileDropdown} trigger={['click']} placement="bottomRight">
          <Avatar className="last" icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
        </Dropdown>
        {/* Mobile Menu Icon (Hamburger) */}
        <div className="mobile-menu-icon" style={{ display: 'none' }}>
          <MenuOutlined
            style={{ fontSize: '24px', cursor: 'pointer', marginLeft: '15px' }}
            onClick={() => setDrawerVisible(true)}
          />
        </div>
      </div>
      {/* Mobile Navigation Drawer */}
      <Drawer
        title="Menu"
        placement="right"
        closable={true}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        className="mobile-drawer"
        bodyStyle={{ padding: 0 }}
      >
        {mobileMenu}
      </Drawer>
      {/* Workbook Component conditionally rendered */}
      {isWorkbookVisible && (
        <div
          style={{
            position: 'absolute',
            top: '60px',
            left: 0,
            width: '100%',
            backgroundColor: 'white',
            zIndex: 999,
            overflowY: 'auto',
            height: 'calc(100vh - 60px)',
          }}
        >
          <Workbook
            initiatives={initiativesData}
            actionItem={workstreamActionsData}
            subworkstream={workstreamData}
            users={usersData}
            filteredData={filteredData}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            currentFilter={currentFilter}
            setCurrentFilter={setCurrentFilter}
            impactCurrentFilter={impactCurrentFilter}
            setImpactCurrentFilter={setImpactCurrentFilter}
            milestoneCurrentFilter={milestoneCurrentFilter}
            setMilestoneCurrentFilter={setMilestoneCurrentFilter}
            kpiCurrentFilter={kpiCurrentFilter}
            setKpiCurrentFilter={setKpiCurrentFilter}
            actionCurrentFilter={actionCurrentFilter}
            setActionCurrentFilter={setActionCurrentFilter}
            attributeFilters={attributeFilters}
            setAttributeFilters={setAttributeFilters}
            filteredActionItems={filteredActionItems}
            filteredMilestones={filteredMilestones}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            groupedInitiativesByHistory={groupedInitiativesByHistory}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            colorByAttribute={colorByAttribute}
            setColorByAttribute={setColorByAttribute}
            initiativeColors={initiativeColors}
            milestoneColors={milestoneColors}
            actionItemColors={actionItemColors}
            groupedActionItems={groupedActionItems}
            groupedMilestones={groupedMilestones}
            groupedImpact={groupedImpact}
            selectedImpactGroup={selectedImpactGroup}
            setSelectedImpactGroup={setSelectedImpactGroup}
            selectedHistoryGroup={selectedHistoryGroup}
            setSelectedHistoryGroup={setSelectedHistoryGroup}
            selectedTimePeriod={selectedTimePeriod}
            setSelectedTimePeriod={setSelectedTimePeriod}
            periodCurrentFilter={periodCurrentFilter}
            setPeriodCurrentFilter={setPeriodCurrentFilter}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
            groupedInitiatives={groupedInitiatives}
            milestones={milestones}
            filterHistory={filterHistory}
            setFilterHistory={setFilterHistory}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
            aggregatedAttributes={aggregatedAttributes}
            setAggregatedAttributes={setAggregatedAttributes}
            sumVisible={sumVisible}
            setSumVisible={setSumVisible}
            finalTotal={grandTotal}
            selectedInfoMenu={selectedInfoMenu}
            setSelectedInfoMenu={setSelectedInfoMenu}
            xAxis={xAxis}
            setXAxis={setXAxis}
            yAxis={yAxis}
            setYAxis={setYAxis}
            setGroupedMilestones={setGroupedMilestones}
            setGroupedInitiatives={setGroupedInitiatives}
            setGroupedActionItems={setGroupedActionItems}
            setGroupedImpact={setGroupedImpact}
            setGroupedInitiativesByHistory={setGroupedInitiativesByHistory}
            subsectors={subsectorData}
            currentUser={currentUser}
          />
        </div>
      )}
      {isDashboardVisible && (
        <div
          style={{
            position: 'absolute',
            top: '60px',
            left: 0,
            width: '100%',
            backgroundColor: 'white',
            zIndex: 999,
            overflowY: 'auto',
            height: 'calc(100vh - 60px)',
          }}
        >
          <Dashboard
            subworkstream={workstreamData}
            users={usersData}
            allInitiative={filteredData}
            actionItem={filteredActionItems}
            setAttributeFilters={setAttributeFilters}
            attributeFilters={attributeFilters}
            setActiveFilterType={setCurrentFilter || setActionCurrentFilter || setMilestoneCurrentFilter}
            filterHistory={filterHistory}
            setFilterHistory={setFilterHistory}
            initiativeColors={initiativeColors}
            setSelectedMenu={setSelectedMenu}
            setAggregatedAttributes={setAggregatedAttributes}
            setColorByAttribute={setColorByAttribute}
            setGroupByAttribute={setSelectedGroup}
            setSelecetdInfoMenu={setSelectedInfoMenu}
            setSelectedImpactGroup={setSelectedImpactGroup}
            setSelectedGroup={setSelectedGroup}
            workstream={workstreamsData}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setYAxis={setYAxis}
            setSelectedDateRange={setSelectedDateRange}
            milestones={milestones}

          />
        </div>
      )}
    </div>
  );
}